import type { AdminAppealDetailDto } from '@carcheck/shared';
import CloseIcon from '@mui/icons-material/Close';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { AppealPriorityBadge } from './AppealPriorityBadge';
import { AppealStatusBadge } from './AppealStatusBadge';
import { tokens } from '../../../design';
import { appealTag, shortId } from '../_lib/displayName';

/**
 * Appeal detail-drawer header (AUT-130): rounded AP-XXXX avatar + title +
 * status badge + priority badge + truncated id + close button. Mirrors
 * `ReportDetailHeader`. No PII in the head — the dual customer / inspector
 * identities (PII-gated) live in the body context grid.
 */
export const AppealDetailHeader = ({
  detail,
  onClose,
}: {
  detail: AdminAppealDetailDto;
  onClose: () => void;
}): ReactElement => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.75,
        p: '18px 24px',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Avatar
        variant="rounded"
        sx={{
          width: 52,
          height: 52,
          fontSize: 13,
          fontWeight: 700,
          fontFamily: 'monospace',
          bgcolor: 'warning.light',
          color: 'warning.dark',
        }}
      >
        {appealTag(detail.appealNumber)}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.3px' }} noWrap>
          {t('appeals.detail.title', { number: `№ ${detail.appealNumber}` })}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
          <AppealStatusBadge status={detail.status} />
          <AppealPriorityBadge priority={detail.priority} />
          <Typography sx={{ fontFamily: 'monospace', fontSize: 12, color: tokens.text.tertiary }}>
            {shortId(detail.id)}
          </Typography>
        </Box>
      </Box>
      <IconButton onClick={onClose} sx={{ ml: 'auto' }} aria-label={t('appeals.detail.close')}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};
